import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Meta } from "entities/common";
import {
  AddUserRequest,
  CompanyItem,
  CompanyListStatistics,
  CompanyStatistics,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  UserItem,
} from "entities/companies";

import { SilentMeta } from "store/silent";

type CompanyState = {
  list: CompanyItem[];
  companyUsers: {
    list: UserItem[];
    meta: Meta;
  };
  company: CompanyItem | null;
  companyStatistics: CompanyStatistics | null;
  statistics: CompanyListStatistics | null;
  isLoading: boolean;
  isUserLoading: boolean;
  isCompanyLoading: boolean;
  isStatisticLoading: boolean;
  isCompanyUsersLoading: boolean;
  isResetted: boolean;
  meta: Meta;
  errors: { detail: Record<string, string> } | null;
  successfullyAddedId: number | null;
  successfullyUpdatedId: number | null;
  successfullyRemovedId: number | null;
};

const initialState: CompanyState = {
  list: [],
  companyUsers: {
    list: [],
    meta: {
      total: 0,
      page: 1,
      page_size: 10,
      total_pages: 0,
    },
  },
  company: null,
  companyStatistics: null,
  statistics: null,
  isLoading: false,
  isUserLoading: false,
  isCompanyLoading: false,
  isStatisticLoading: false,
  isCompanyUsersLoading: false,
  isResetted: false,
  meta: {
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
  },
  errors: null,
  successfullyAddedId: null,
  successfullyUpdatedId: null,
  successfullyRemovedId: null,
};

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    getCompanyListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isLoading = true;
      },
      prepare: (query = "", meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getCompanyListSuccess(state, action: { payload: { items: CompanyItem[] } & Meta }) {
      state.list = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages,
      };
      state.isLoading = false;
    },
    getCompanyListFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isLoading = false;
      state.errors = action.payload;
    },

    getCompanyListStatisticsRequest: {
      reducer(state, action: PayloadAction<undefined, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticLoading = true;
      },
      prepare: (meta: SilentMeta = {}) => ({ payload: undefined, meta }),
    },
    getCompanyListStatisticsSuccess(state, action: { payload: CompanyListStatistics }) {
      state.statistics = action.payload;
      state.isStatisticLoading = false;
    },
    getCompanyListStatisticsFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isStatisticLoading = false;
      state.errors = action.payload;
    },

    getCompanyStatisticsRequest: {
      reducer(state, action: PayloadAction<number, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticLoading = true;
      },
      prepare: (companyId: number, meta: SilentMeta = {}) => ({ payload: companyId, meta }),
    },
    getCompanyStatisticsSuccess(state, action: { payload: CompanyStatistics }) {
      state.companyStatistics = action.payload;
      state.isStatisticLoading = false;
    },
    getCompanyStatisticsFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isStatisticLoading = false;
      state.errors = action.payload;
    },

    getCompanyRequest: {
      reducer(state, action: PayloadAction<number, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isCompanyLoading = true;
      },
      prepare: (companyId: number, meta: SilentMeta = {}) => ({ payload: companyId, meta }),
    },
    getCompanySuccess(state, action: { payload: CompanyItem }) {
      state.isCompanyLoading = false;
      state.company = action.payload;
    },
    getCompanyFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isCompanyLoading = false;
      state.errors = action.payload;
    },

    getCompanyUsersRequest(state, action: { payload: { companyId: number; query?: string } }) {
      void action.payload;
      state.isCompanyUsersLoading = true;
    },
    getCompanyUsersSuccess(state, action: { payload: { items: UserItem[] } & Meta }) {
      state.isCompanyUsersLoading = false;
      state.companyUsers.list = action.payload.items;
      state.companyUsers.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages,
      };
    },
    getCompanyUsersFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isCompanyUsersLoading = false;
      state.errors = action.payload;
    },

    createCompanyRequest(state, action: { payload: CreateCompanyRequest }) {
      void action.payload;
      state.isLoading = true;
      state.successfullyAddedId = null;
    },
    createCompanySuccess(state, action: { payload: CompanyItem }) {
      state.list.unshift(action.payload);
      state.isLoading = false;
      state.successfullyAddedId = action.payload.id;
    },
    createCompanyFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isLoading = false;
      state.errors = action.payload;
    },

    updateCompanyRequest(state, action: { payload: { id: number; body: UpdateCompanyRequest } }) {
      void action.payload;
      state.isLoading = true;
      state.successfullyUpdatedId = null;
    },
    updateCompanySuccess(state, action: { payload: CompanyItem }) {
      state.isLoading = false;

      if (state.company?.id === action.payload.id) {
        state.company = {
          ...state.company,
          ...action.payload,
        };
      }

      state.successfullyUpdatedId = action.payload.id;
    },
    updateCompanyFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isLoading = false;
      state.errors = action.payload;
    },

    removeCompanyRequest(state, action: { payload: { id: number } }) {
      void action.payload;
      state.isLoading = true;
      state.successfullyRemovedId = null;
    },
    removeCompanySuccess(state, action: { payload: number }) {
      state.isLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removeCompanyFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isLoading = false;
      state.errors = action.payload;
    },

    addCompanyUserRequest(state, action: { payload: { companyId: number; body: AddUserRequest } }) {
      void action.payload;
      state.isUserLoading = true;
    },
    addCompanyUserSuccess(state, action: { payload: UserItem }) {
      state.isUserLoading = false;
      state.successfullyAddedId = action.payload.id;
    },
    addCompanyUserFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isUserLoading = false;
      state.errors = action.payload;
    },

    updateCompanyUserRequest(state, action: { payload: { companyId: number; userId: number; body: AddUserRequest } }) {
      void action.payload;
      state.isUserLoading = true;
    },
    updateCompanyUserSuccess(state, action: { payload: UserItem }) {
      state.isUserLoading = false;
      state.successfullyUpdatedId = action.payload.id;
    },
    updateCompanyUserFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isUserLoading = false;
      state.errors = action.payload;
    },

    removeCompanyUserRequest(state, action: { payload: { companyId: number; userId: number } }) {
      void action.payload;
      state.isUserLoading = true;
    },
    removeCompanyUserSuccess(state, action: { payload: number }) {
      state.isUserLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removeCompanyUserFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isUserLoading = false;
      state.errors = action.payload;
    },

    resendCompanyUserInvitationRequest(state, action: { payload: { companyId: number; userId: number } }) {
      void action.payload;
      state.isUserLoading = true;
    },
    resendCompanyUserInvitationSuccess(state) {
      state.isUserLoading = false;
      state.isResetted = true;
    },
    resendCompanyUserInvitationFailure(state, action: { payload: { detail: Record<string, string> } }) {
      state.isUserLoading = false;
      state.errors = action.payload;
    },

    changePage(state, action: { payload: number }) {
      state.meta.page = action.payload;
    },
    changeCompanyUsersPage(state, action: { payload: number }) {
      state.companyUsers.meta.page = action.payload;
    },

    resetCompanyList(state) {
      state.list = [];
      state.meta = {
        total: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
      };
    },
    resetSuccessfullyAddedCompany(state) {
      state.successfullyAddedId = null;
    },
    resetSuccessfullyUpdatedCompany(state) {
      state.successfullyUpdatedId = null;
    },
    resetSuccessfullyRemovedCompany(state) {
      state.successfullyRemovedId = null;
    },
    resetErrorsCompany(state) {
      state.errors = null;
    },
    resetCompany(state) {
      state.company = null;
    },
    resetIsResetted(state) {
      state.isResetted = false;
    },
  },
});

export const {
  getCompanyRequest,
  getCompanySuccess,
  getCompanyFailure,
  getCompanyListRequest,
  getCompanyListSuccess,
  getCompanyListFailure,
  getCompanyUsersRequest,
  getCompanyUsersSuccess,
  getCompanyUsersFailure,
  createCompanyRequest,
  createCompanySuccess,
  createCompanyFailure,
  updateCompanyRequest,
  updateCompanySuccess,
  updateCompanyFailure,
  removeCompanyRequest,
  removeCompanySuccess,
  removeCompanyFailure,
  resetSuccessfullyAddedCompany,
  resetSuccessfullyUpdatedCompany,
  resetSuccessfullyRemovedCompany,
  resetIsResetted,
  resetErrorsCompany,
  resetCompany,
  getCompanyListStatisticsRequest,
  getCompanyListStatisticsSuccess,
  getCompanyListStatisticsFailure,
  changePage,
  changeCompanyUsersPage,
  addCompanyUserRequest,
  addCompanyUserSuccess,
  addCompanyUserFailure,
  updateCompanyUserRequest,
  updateCompanyUserSuccess,
  updateCompanyUserFailure,
  removeCompanyUserRequest,
  removeCompanyUserSuccess,
  removeCompanyUserFailure,
  resendCompanyUserInvitationRequest,
  resendCompanyUserInvitationSuccess,
  resendCompanyUserInvitationFailure,
  getCompanyStatisticsRequest,
  getCompanyStatisticsSuccess,
  getCompanyStatisticsFailure,
  resetCompanyList,
} = companiesSlice.actions;
export default companiesSlice.reducer;
